import { isAxiosError } from 'axios';

/**
 * Central place for browser logging: production builds should not emit tokens, response bodies,
 * or full axios error objects (they may include request config with Authorization).
 */
export const isDev = process.env.NODE_ENV === 'development';

/** Strip query string — tokens and secrets often appear in ?query=… */
export function pathOnlyForLog(url: string): string {
    if (!url || typeof url !== 'string') return 'unknown';
    const q = url.indexOf('?');
    return q === -1 ? url : url.slice(0, q);
}

/**
 * Use instead of console.error(...) for non-axios failures: in production logs only scope + message,
 * not PHI, API bodies, or arbitrary objects.
 */
export function safeConsoleError(scope: string, message: string, ...detail: unknown[]): void {
    if (isDev) {
        console.error(`[${scope}]`, message, ...detail);
    } else {
        console.error(`[${scope}]`, message);
    }
}

export function devLog(...args: unknown[]): void {
    if (isDev) console.log(...args);
}

export function devWarn(...args: unknown[]): void {
    if (isDev) console.warn(...args);
}

export function devError(...args: unknown[]): void {
    if (isDev) console.error(...args);
}

/** Always logs a short line; never passes through raw Error objects in production. */
export function logClientError(scope: string, err: unknown): void {
    const msg =
        err instanceof Error
            ? err.message
            : typeof err === 'string'
              ? err
              : 'error';
    if (isDev) {
        console.error(`[${scope}]`, err);
    } else {
        console.error(`[${scope}]`, msg);
    }
}

/**
 * Axios failures: in dev, log structured fields; in prod, status (or generic) only — no URLs/paths
 * that may embed identifiers, and never the full axios error (may contain Authorization).
 */
export function logAxiosFailure(scope: string, error: unknown): void {
    if (!isAxiosError(error)) {
        logClientError(scope, error);
        return;
    }
    const status = error.response?.status;
    if (isDev) {
        const pathOnly = pathOnlyForLog(error.config?.url ?? '') || 'unknown';
        console.error(`[${scope}]`, {
            status,
            path: pathOnly,
            message: error.message,
            code: error.code,
        });
        return;
    }
    if (typeof status === 'number') {
        console.error(`[${scope}] HTTP ${status}`);
    } else {
        console.error(`[${scope}]`, error.message || 'request failed');
    }
}
